'use client';

import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  MenuItem,
  OutlinedInput,
  Radio,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableRow,
  Typography,
} from '@mui/material';
import {
  BackupZeroEtlIdentityColumnField,
  BackupZeroEtlRoutingMode,
} from '@repo/api-gw-sdk';
import { useState } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';
import {
  OptionListItem,
  OptionListItemContent,
} from '@/components/shared/optionListItem';
import { BorderlessTableCell } from '@/components/table/components';

import {
  FULLY_QUALIFIED_NAMESPACE,
  FULLY_QUALIFIED_TABLE,
  type ZeroEtlPolicyEditState,
} from './ZeroEtlPolicyCreationFlow';

const IDENTITY_COLUMN_OPTIONS = Object.values(BackupZeroEtlIdentityColumnField);

const IDENTITY_COLUMN_VALUES = new Set<string>(IDENTITY_COLUMN_OPTIONS);

function isIdentityColumnField(
  value: string
): value is BackupZeroEtlIdentityColumnField {
  return IDENTITY_COLUMN_VALUES.has(value);
}

const TEMPLATE_VARIABLES = [
  '{source_cloud}',
  '{source_account_id}',
  '{source_region}',
  '{resource_id}',
  '{database_name}',
  '{schema_name}',
  '{table_name}',
];

type RoutingPolicyFields = Pick<
  ZeroEtlPolicyEditState,
  | 'routingMode'
  | 'namespaceTemplate'
  | 'tableTemplate'
  | 'identityColumns'
  | 'showAdditionalColumnsSection'
>;

interface RoutingPolicyPanelProps {
  state: ZeroEtlPolicyEditState;
  onChange: (updates: Partial<RoutingPolicyFields>) => void;
}

export const ZeroEtlRouting = ({
  state,
  onChange,
}: RoutingPolicyPanelProps) => {
  return (
    <Stack gap='40px' width='100%' minWidth={0}>
      <Stack className='px-[min(100px,5vw)]' gap='24px'>
        <Typography variant='h3'>Table mapping</Typography>
        <Typography variant='body1'>
          Define how your data is organized into Iceberg tables in the AWS Glue
          Catalog.
        </Typography>
      </Stack>
      <Box className='px-[min(100px,5vw)]'>
        <RoutingModeSelector state={state} onChange={onChange} />
      </Box>
      <AddColumnsSection state={state} onChange={onChange} />
    </Stack>
  );
};

const RoutingModeSelector = ({
  state,
  onChange,
}: {
  state: ZeroEtlPolicyEditState;
  onChange: (updates: Partial<RoutingPolicyFields>) => void;
}) => {
  const isManual =
    state.routingMode ===
    BackupZeroEtlRoutingMode.BACKUP_ZERO_ETL_ROUTING_MODE_MANUAL;

  return (
    <Stack gap='24px' overflow='auto'>
      <Typography>Naming</Typography>
      <List disablePadding>
        <OptionListItem
          isChecked={!isManual}
          onClick={() =>
            onChange({
              routingMode:
                BackupZeroEtlRoutingMode.BACKUP_ZERO_ETL_ROUTING_MODE_FULLY_QUALIFIED,
            })
          }
        >
          <FormControlLabel
            checked={!isManual}
            onClick={() => {
              onChange({
                routingMode:
                  BackupZeroEtlRoutingMode.BACKUP_ZERO_ETL_ROUTING_MODE_FULLY_QUALIFIED,
              });
            }}
            control={<Radio sx={{ padding: '0' }} />}
            sx={{ alignItems: 'flex-start', gap: '12px' }}
            label={
              <Stack gap='4px'>
                <Typography variant='subtitle1'>Automatic</Typography>
                <Typography variant='body2'>
                  Tables are written using a deterministic path derived from the
                  source resource.
                </Typography>
              </Stack>
            }
          />

          <OptionListItemContent isExpanded={!isManual}>
            <Stack
              width='100%'
              sx={{ backgroundColor: 'background.secondary' }}
              padding='16px'
              gap='12px'
              divider={<Divider sx={{ borderStyle: 'dashed' }} />}
            >
              <ReadOnlyField
                label='Database name'
                value={FULLY_QUALIFIED_NAMESPACE}
              />
              <ReadOnlyField label='Table name' value={FULLY_QUALIFIED_TABLE} />
            </Stack>
          </OptionListItemContent>
        </OptionListItem>
        <OptionListItem
          isChecked={isManual}
          onClick={() =>
            onChange({
              routingMode:
                BackupZeroEtlRoutingMode.BACKUP_ZERO_ETL_ROUTING_MODE_MANUAL,
            })
          }
        >
          <FormControlLabel
            checked={isManual}
            onClick={() => {
              onChange({
                routingMode:
                  BackupZeroEtlRoutingMode.BACKUP_ZERO_ETL_ROUTING_MODE_MANUAL,
              });
            }}
            control={<Radio sx={{ padding: '0' }} />}
            sx={{ alignItems: 'flex-start', gap: '12px' }}
            label={
              <Stack gap='4px'>
                <Typography variant='subtitle1'>Custom</Typography>
                <Typography variant='body2'>
                  Define how your data is organized into databases and tables in
                  AWS Glue Catalog.
                </Typography>
              </Stack>
            }
          />
          <OptionListItemContent isExpanded={isManual}>
            <Stack
              gap='24px'
              divider={<Divider sx={{ borderStyle: 'dashed' }} />}
            >
              <Stack
                gap='4px'
                direction='row'
                alignItems='text-start'
                width='100%'
              >
                <Typography width='120px'>Database name</Typography>
                <Stack gap='8px' width='100%'>
                  <OutlinedInput
                    value={state.namespaceTemplate}
                    onChange={(e) =>
                      onChange({ namespaceTemplate: e.target.value })
                    }
                    placeholder='{source_cloud}.{database_name}.{schema_name}'
                    size='small'
                    sx={{ fontFamily: 'monospace', width: '100%' }}
                  />
                  <PreviewTemplate template={state.namespaceTemplate} />
                </Stack>
              </Stack>
              <Stack
                gap='4px'
                direction='row'
                alignItems='text-start'
                width='100%'
              >
                <Typography width='120px'>Table name</Typography>
                <Stack gap='8px' width='100%'>
                  <OutlinedInput
                    value={state.tableTemplate}
                    onChange={(e) =>
                      onChange({ tableTemplate: e.target.value })
                    }
                    placeholder='{table_name}'
                    size='small'
                    sx={{ fontFamily: 'monospace' }}
                  />
                  <PreviewTemplate template={state.tableTemplate} />
                </Stack>
              </Stack>
            </Stack>
          </OptionListItemContent>
        </OptionListItem>
      </List>
    </Stack>
  );
};

const PreviewTemplate = ({ template }: { template: string }) => {
  return (
    <Stack
      width='100%'
      sx={{ backgroundColor: 'background.secondary' }}
      padding='16px'
    >
      <ColoredVariables text={template} />
    </Stack>
  );
};

const AddColumnsSection = ({
  state,
  onChange,
}: {
  state: ZeroEtlPolicyEditState;
  onChange: (updates: Partial<RoutingPolicyFields>) => void;
}) => {
  const selectedFields = new Set(state.identityColumns.map((ic) => ic.field));
  const toggleField = (field: BackupZeroEtlIdentityColumnField) => {
    const updated = selectedFields.has(field)
      ? state.identityColumns.filter((ic) => ic.field !== field)
      : [
          ...state.identityColumns,
          { field, columnName: field.toLowerCase(), partition: false },
        ];
    onChange({ identityColumns: updated });
  };

  const togglePartition = (field: BackupZeroEtlIdentityColumnField) => {
    onChange({
      identityColumns: state.identityColumns.map((ic) =>
        ic.field === field ? { ...ic, partition: !ic.partition } : ic
      ),
    });
  };

  return (
    <Stack gap='24px' paddingTop='16px'>
      <Divider />
      <Stack gap='24px' className='px-[min(100px,5vw)]'>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Typography variant='subtitle1'>
            Add metadata columns from resource properties
          </Typography>
          <Switch
            checked={state.showAdditionalColumnsSection}
            onChange={(_, checked) =>
              onChange({ showAdditionalColumnsSection: checked })
            }
          />
        </Stack>
        {state.showAdditionalColumnsSection && (
          <>
            <Typography variant='body2' color='text.secondary'>
              Add columns with resource properties (e.g., account, region) that
              will be added to your final tables.
            </Typography>
            <Select
              value=''
              displayEmpty
              size='small'
              onChange={(e) => {
                const value = e.target.value;
                if (
                  isIdentityColumnField(value) &&
                  !selectedFields.has(value)
                ) {
                  toggleField(value);
                }
              }}
              renderValue={() => (
                <Typography variant='body2' color='text.secondary'>
                  Add a field...
                </Typography>
              )}
              sx={{
                fontFamily: 'monospace',
              }}
            >
              {IDENTITY_COLUMN_OPTIONS.filter(
                (f) => !selectedFields.has(f)
              ).map((field) => (
                <MenuItem key={field} value={field}>
                  {field.toLowerCase()}
                </MenuItem>
              ))}
            </Select>
            {state.identityColumns.length > 0 && (
              <Table sx={{ tableLayout: 'fixed' }}>
                <TableBody>
                  <TableRow sx={{ height: '32px' }}>
                    <BorderlessTableCell sx={{ width: '40px' }} />
                    <BorderlessTableCell sx={{ width: '80px' }}>
                      <Typography variant='body2' color='text.secondary'>
                        Partition
                      </Typography>
                    </BorderlessTableCell>
                    <BorderlessTableCell>
                      <Typography variant='body2' color='text.secondary'>
                        Column
                      </Typography>
                    </BorderlessTableCell>
                  </TableRow>
                  {state.identityColumns.map((ic) => (
                    <TableRow key={ic.field} sx={{ height: '40px' }}>
                      <BorderlessTableCell sx={{ width: '40px' }}>
                        <IconButton
                          size='small'
                          onClick={() => toggleField(ic.field)}
                        >
                          <i className='material-symbols-close-rounded text-sm' />
                        </IconButton>
                      </BorderlessTableCell>
                      <BorderlessTableCell sx={{ width: '80px' }}>
                        <Checkbox
                          checked={!!ic.partition}
                          onChange={() => togglePartition(ic.field)}
                          size='small'
                          sx={{ padding: '2px' }}
                        />
                      </BorderlessTableCell>
                      <BorderlessTableCell>
                        <Typography variant='body2'>
                          {ic.field.toLowerCase()}
                        </Typography>
                      </BorderlessTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </Stack>
      <Divider />
    </Stack>
  );
};

const ReadOnlyField = ({ label, value }: { label: string; value: string }) => (
  <Stack gap='4px' direction='row' alignItems='center'>
    <Typography width='100px' variant='body2'>
      {label}
    </Typography>
    <ColoredVariables text={value} />
  </Stack>
);

export const ColoredVariables = ({
  text,
  variant = 'subtitle1',
}: {
  text: string;
  variant?: 'subtitle1' | 'subtitle2';
}) => {
  if (!text) {
    return <Typography variant='body1'>Preview will appear here</Typography>;
  }

  const parts = text.split(/(\{[^}]*\})/g);

  return (
    <Typography variant={variant} sx={{ wordBreak: 'break-word' }}>
      {parts.map((part, index) =>
        part.startsWith('{') && part.endsWith('}') ? (
          <span
            key={index}
            style={{ color: 'var(--mui-palette-custom-BLUE-01)' }}
          >
            {part}
          </span>
        ) : (
          part
        )
      )}
    </Typography>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ChangeRoutingModeDialog = ({
  mode,
  onCancel,
  onSelect,
}: {
  mode: BackupZeroEtlRoutingMode;
  onCancel: () => void;
  onSelect: (mode: BackupZeroEtlRoutingMode) => void;
}) => {
  const [modeValue, setModeValue] = useState(mode);

  return (
    <MainDialog
      sx={{ minWidth: '720px', overflowX: 'hidden' }}
      isOpen={true}
      isLoading={false}
      header={{
        title: 'Routing mode',
        onClose: onCancel,
      }}
      footer={{
        primaryButton: {
          'data-testid': 'approve-change-routing-mode',
          text: 'Select',
          onClick: () =>
            mode === modeValue ? onCancel() : onSelect(modeValue),
        },
        secondaryButton: {
          'data-testid': 'cancel-change-routing-mode',
          onClick: onCancel,
          text: 'Cancel',
        },
      }}
    >
      <Stack gap='40px' className='my-[20px]'>
        <Stack direction='row' className='mx-[40px]' gap='60px'>
          <Stack gap='16px' flex={1}>
            <FormControlLabel
              control={<Radio className='mr-[12px]' />}
              label='Fully qualified'
              checked={
                modeValue ===
                BackupZeroEtlRoutingMode.BACKUP_ZERO_ETL_ROUTING_MODE_FULLY_QUALIFIED
              }
              onChange={() =>
                setModeValue(
                  BackupZeroEtlRoutingMode.BACKUP_ZERO_ETL_ROUTING_MODE_FULLY_QUALIFIED
                )
              }
            />
            <Typography className='ml-[48px] w-[200px]' variant='body2'>
              Tables are written using a deterministic path derived from the
              source resource.
            </Typography>
          </Stack>
          <Divider orientation='vertical' flexItem />
          <Stack
            sx={{ backgroundColor: 'background.secondary' }}
            padding='16px'
            gap='12px'
            divider={<Divider sx={{ borderStyle: 'dashed' }} />}
            flex={1}
          >
            <ReadOnlyField
              label='Database name'
              value={FULLY_QUALIFIED_NAMESPACE}
            />
            <ReadOnlyField label='Table name' value={FULLY_QUALIFIED_TABLE} />
          </Stack>
        </Stack>
        <Divider />
        <Stack direction='row' className='mx-[40px]' gap='60px'>
          <Stack gap='16px' flex={1}>
            <FormControlLabel
              control={<Radio className='mr-[12px]' />}
              label='Manual'
              checked={
                modeValue ===
                BackupZeroEtlRoutingMode.BACKUP_ZERO_ETL_ROUTING_MODE_MANUAL
              }
              onChange={() =>
                setModeValue(
                  BackupZeroEtlRoutingMode.BACKUP_ZERO_ETL_ROUTING_MODE_MANUAL
                )
              }
            />
            <Typography className='ml-[48px] w-[200px]' variant='body2'>
              Define how your data is organized into databases and tables in AWS
              Glue Catalog.
            </Typography>
          </Stack>
          <Divider orientation='vertical' flexItem />
          <Stack gap='4px' flex={1}>
            <Typography variant='body2' color='text.secondary'>
              Available variables
            </Typography>
            {TEMPLATE_VARIABLES.map((v) => (
              <Typography
                key={v}
                variant='body2'
                sx={{ fontFamily: 'monospace' }}
              >
                {v}
              </Typography>
            ))}
          </Stack>
        </Stack>
      </Stack>
    </MainDialog>
  );
};
